import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import { Box, Grid } from '@mui/material';
import { CommonActionsV1, CommonEmptyDataV1, CommonExpandedContentV1 } from 'gemlux-common-components';
import { SortOrder } from '../../utils/enums/sort-order.enum';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { ViewType } from '../../utils/enums/view-type.enum';
import useEnv from '../../utils/use-env-hook';
import { gemActions, IGem } from '../../redux/gem/gemSlice';
import { MY_GEM_FAV_ROUTE, MY_GEM_VAULT_ROUTE } from '../../utils/routes';
import PlaceholderGemSvg from '../../assets/img/gem-svgrepo-com.svg';
import CustomTable, { ColumnProps } from '../../components/V1/CustomTable/CustomTable';
import EmptyBoxIcon from '../../assets/img/V1/box.svg';
import GemGridView from '../../components/V1/GridView/GemGridView';
import { Field, renderColorDot } from '../MyGemVault/MyGemVault';
import { modelAction } from '../../redux/models/modelsSlice';
import { Roles } from '../../utils/enums/roles.enum';
import { useAuth } from '../../context/AuthContext';
import { Permission } from '../../utils/enums/permission.enum';
import { AiStatus } from '../../utils/enums/gem-ai-status.enum';

const columns: ColumnProps[] = [
  { key: 'appearanceImage', name: 'Image', enableSort: false, align: 'left', width: '5%', resizable: false },
  {
    key: 'systemId',
    name: 'GemID',
    enableSort: true,
    align: 'left',
    width: '7%',
    resizable: false,
  },
  { key: 'species', name: 'Species', enableSort: true, align: 'left', width: '7%', resizable: false },
  { key: 'color', name: 'Primary Color', enableSort: true, align: 'left', width: '8%', resizable: false },
  {
    key: 'shape',
    name: 'Shape',
    enableSort: true,
    align: 'left',
    width: '7%',
    resizable: false,
  },
  { key: 'cut', name: 'Cut', enableSort: true, align: 'left', width: '8%', resizable: false },

  {
    key: 'weight',
    name: 'Weight (ct)',
    enableSort: true,
    align: 'left',
    width: '10%',
    resizable: false,
  },
  { key: 'clarity', name: 'Clarity', enableSort: true, align: 'left', width: '7%', resizable: false },
  {
    key: 'treatment',
    name: 'Treatment',
    enableSort: true,
    align: 'left',
    width: '7%',
    resizable: false,
  },
  { key: 'user', name: 'Record Owner', enableSort: true, align: 'left', width: '12%', resizable: false },
  {
    key: 'organizationData',
    name: 'Company Name',
    enableSort: true,
    align: 'left',
    width: '12%',
    resizable: false,
  },
  { key: 'action', name: 'Actions', enableSort: false, align: 'right', width: '7%', resizable: false },
];

const tileColumns = [
  'Dimensions(mm)',
  'Weight (ct)',
  'Species',
  'Cut',
  'Clarity',
  'Shape',
  'Origin',
  'Treatment',
  'Owner',
  'Company Name',
];

const tileColumnValues = [
  'dimensions',
  'weight',
  'species',
  'cut',
  'clarity',
  'shape',
  'origin',
  'treatment',
  'user',
  'organization',
];

const expandedRowColumns = [
  'GemID',
  'Species',
  'Primary Color',
  'Secondary Color',
  'Intensity',
  'Shape',
  'Weight (ct)',
  'Clarity',
  'Special Color Descriptor',
  'Cut',
  'Dimensions (mm)',
  'Treatment',
  'Gem Lab',
  'Origin',
  'Listing Date',
  'Company Name',
  'Record Owner',
];

const expandedRowValues = [
  'systemId',
  'species',
  'color',
  'secondaryColor',
  'intensity',
  'shape',
  'weight',
  'clarity',
  'specialDescriptor',
  'cut',
  'dimensions',
  'treatment',
  'certificate',
  'origin',
  'scannedDate',
  'organization',
  'user',
];
// const topRighField: Field = { column: 'Fingerprint UGIN', value: 'uginId' };

export default function MyGemFav() {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const navigate = useNavigate();
  const itemsPerPageParam = searchParams.get('items-per-page');
  const sortByParam = searchParams.get('sort-by');
  const sortOrderParam = searchParams.get('sort-order');
  const [page, setPage] = React.useState<number>(pageParam ? parseInt(pageParam, 10) : 1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(itemsPerPageParam ? parseInt(itemsPerPageParam, 10) : 10);
  const [sortBy, setSortBy] = React.useState<string>(sortByParam ?? 'createdAt');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>((sortOrderParam as SortOrder) ?? SortOrder.DESC);
  const [expandedRows, setExpandedRows] = React.useState<string[]>([]);
  const { componentStatus, gems, totalGemsCount, favourites, selectedFilterOptions } = useAppSelector(
    (state: RootState) => state.gem,
  );
  const getGemsLoading = Boolean(_.get(componentStatus, 'getGemsLoading', false));
  const addRemoveFavouriteGemLoading = Boolean(_.get(componentStatus, 'addRemoveFavouriteGemLoading', false));
  const search = String(_.get(componentStatus, 'search', ''));
  const dispatch = useAppDispatch();
  const viewStatus = String(_.get(componentStatus, 'view', ViewType.LIST));
  const { baseUrl } = useEnv();
  const specialListColumns = ['Intensity', 'Primary Color', 'Secondary Color'];
  const { user } = useAuth();
  const enableEditForSubordinates =
    (user?.role === Roles.SAAS_USER && user.permissions.includes(Permission.MANAGE_MY_GEM_VAULT)) ||
    user?.role === Roles.SAAS_ADMIN ||
    user?.role === Roles.NCC_USER;
  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (rows: number) => {
    setItemsPerPage(rows);
    setPage(1);
  };

  const toggleFavourite = (gemId: string, favouriteId?: string) => {
    if (!favourites.includes(gemId)) {
      dispatch(gemActions.addRemoveFavouriteGemAsync({ gemId }));
    } else {
      dispatch(gemActions.addRemoveFavouriteGemAsync({ gemId, favouriteId }));
    }
  };

  const togglePublic = (gemId: string) => {
    dispatch(gemActions.togglePublicGemAsync({ gemId }));
  };

  const handleEditGem = (gem: IGem) => {
    if (!enableEditForSubordinates) {
      return dispatch(modelAction.setIsPermissionDeniedModelOpen(true));
    }
    if (gem.is_scanned && gem.aiStatus === AiStatus.COMPLETED) {
      dispatch(gemActions.openEditGemConfirmationModel({ gem }));
    } else {
      dispatch(gemActions.openEditGemModel({ gem }));
    }
  };
  const handleFullDetail = (gemId: string) => {
    navigate(`${MY_GEM_FAV_ROUTE}/${gemId}`);
  };

  const toggleExpandRow = (gemId: string) => {
    setExpandedRows((prevExpandedRows) =>
      prevExpandedRows.includes(gemId) ? prevExpandedRows.filter((id) => id !== gemId) : [...prevExpandedRows, gemId],
    );
  };

  const getFormattedGemList = (gemList: IGem[]) =>
    gemList.map((gem: IGem) => {
      const isExpanded = expandedRows.includes(gem.gemId);
      const isFavourite = favourites.includes(gem.gemId);
      const { isPublic } = gem;
      const showPublicIcon = gem.is_scanned && gem.organizationId === user.organizationId;
      return {
        systemId: gem.systemId ?? 'NA',
        uginId: gem.uginId || 'NIL',
        appearanceImage: (
          <Box
            component="img"
            src={`${baseUrl}/${gem.appearanceImage}`}
            width={20}
            height={20}
            borderRadius="5px"
            onError={(event: React.SyntheticEvent<HTMLImageElement, Event>) => {
              const img = event.currentTarget as HTMLImageElement;
              img.src = PlaceholderGemSvg;
            }}
          />
        ),
        species: gem.species ?? 'NA',
        weight: gem.weight.toFixed(2) ?? 'NA',
        color: (
          <Box display="flex" alignItems="center">
            {renderColorDot(gem.color)}
            <Box ml={0.5}>{gem.color ?? 'NA'}</Box>
          </Box>
        ),
        length: gem.length ?? 'NA',
        width: gem.width ?? 'NA',
        depth: gem.depth ?? 'NA',
        dimensions:
          gem.length && gem.width && gem.depth
            ? `${gem.length?.toString().padStart(2, '0')} x
             ${gem.width?.toString().padStart(2, '0')} x ${gem.depth?.toString().padStart(2, '0')}`
            : (gem.dimensions ?? 'NA'),
        cut: gem.cut ?? 'NA',
        clarity: gem.clarity ?? 'NA',
        treatment: gem.treatment ?? 'NA',
        shape: gem.shape ?? 'NA',
        origin: gem.origin ?? 'NA',
        certificate: gem.certificate ?? 'NA',
        user: gem.user ?? 'NA',
        organizationData: gem.organization ?? 'NA',
        action: (
          <CommonActionsV1
            toggleFavourite={() => toggleFavourite(gem.gemId, gem.favouriteId)}
            togglePublic={() => togglePublic(gem.gemId)}
            isFavourite={isFavourite}
            isPublic={isPublic}
            isExpanded={isExpanded}
            isGemVault={false}
            isFromAdminPanel={false}
            toggleExpandRow={() => toggleExpandRow(gem.gemId)}
            isGemBelongToOrganization={false}
            showPublicIcon={showPublicIcon}
          />
        ),
        expandedContent: isExpanded && (
          <CommonExpandedContentV1
            gem={{
              ...gem,
              uginId: gem.uginId,
              dimensions:
                gem.length && gem.width && gem.depth
                  ? `${gem.length && gem.length.toFixed(2)} x ${gem.width.toFixed(2)} x ${gem.depth.toFixed(2)}`
                  : gem.dimensions,
              weight: gem.weight.toFixed(2) ?? 'NA',
              user: gem.user,
              organization: gem.organization,
              scannedDate: gem.scannedDate ?? new Date(gem.createdAt).toLocaleDateString('en-GB'),
              appearanceImage: (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  component="img"
                  src={`${baseUrl}/${gem.appearanceImage}`}
                  width={100}
                  height={100}
                  borderRadius="5px"
                  onError={(event: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    const img = event.currentTarget as HTMLImageElement;
                    img.src = PlaceholderGemSvg;
                  }}
                />
              ),
            }}
            handleEditGem={() => handleEditGem(gem)}
            toggleExpandRow={() => toggleExpandRow(gem.gemId)}
            toggleFavourite={() => toggleFavourite(gem.gemId, gem.favouriteId)}
            togglePublic={() => togglePublic(gem.gemId)}
            isFavourite={isFavourite}
            isPublic={isPublic}
            handleFullDetail={() => handleFullDetail(gem.gemId)}
            isGemVault
            expandedRowColumns={expandedRowColumns}
            expandedRowValues={expandedRowValues}
            isGemBelongToOrganization={!!gem.organizationId}
            title={`${gem.species} ${gem.color} ${gem.shape} ${gem.weight}ct`}
            specialListColumns={specialListColumns}
            isFromAdminPanel={false}
            showPublicIcon={showPublicIcon}
          />
        ),
      };
    });

  const getFormattedGemTileList = (gemList: IGem[]) =>
    gemList.map((gem: IGem) => {
      const isFavourite = favourites.includes(gem.gemId);
      return {
        ...gem,
        dimensions:
          gem.length && gem.width && gem.depth
            ? `${gem.length && gem.length.toFixed(2)} x ${gem.width.toFixed(2)} x ${gem.depth.toFixed(2)}`
            : gem.dimensions,
        weight: gem.weight.toFixed(2) ?? 'NA',
        appearanceImageView: (
          <Grid
            item
            padding={{ xs: '12px', sm: '12px', md: '12px' }}
            display="flex"
            justifyContent="center"
            alignItems="center"
            xs={12}
            sm={12}
            minHeight="250px"
          >
            <Box
              component="img"
              src={`${baseUrl}/${gem.appearanceImage}`}
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '5px',
                objectFit: 'contain',
                imageRendering: 'auto',
              }}
              onError={(event: React.SyntheticEvent<HTMLImageElement, Event>) => {
                const img = event.currentTarget as HTMLImageElement;
                img.src = PlaceholderGemSvg;
              }}
            />
          </Grid>
        ),
        isFavourite,
      };
    });

  useEffect(() => {
    if (viewStatus === ViewType.LIST) {
      setItemsPerPage(10);
    } else if (viewStatus === ViewType.GRID) {
      setItemsPerPage(12);
    }
  }, [viewStatus]);

  useEffect(() => {
    if (search || selectedFilterOptions || sortOrder || sortBy || viewStatus) {
      setPage(1);
    }
  }, [search, selectedFilterOptions, sortOrder, sortBy, viewStatus]);

  useEffect(() => {
    setPage(pageParam ? parseInt(pageParam, 10) : 1);
  }, [pageParam]);

  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      'items-per-page': itemsPerPage.toString(),
      'sort-by': sortBy,
      ...(viewStatus === ViewType.LIST && { 'sort-order': sortOrder }),
      search,
    });
  }, [page, itemsPerPage, search, sortBy, sortOrder, viewStatus]);

  useEffect(() => {
    if (!addRemoveFavouriteGemLoading) {
      dispatch(gemActions.resetGetGems());
      dispatch(gemActions.resetFavourites());
      dispatch(
        gemActions.getFavGemsAsync({
          limit: itemsPerPage,
          page,
          search,
          selectedFilterOptions,
          sortBy,
          sortOrder,
        }),
      );
    }
  }, [viewStatus, page, itemsPerPage, search, selectedFilterOptions, sortBy, sortOrder, addRemoveFavouriteGemLoading]);

  if (gems.length === 0 && !getGemsLoading && !search && selectedFilterOptions.length === 0) {
    return (
      <CommonEmptyDataV1
        title="No Gems Favorited"
        icon={<Box component="img" src={EmptyBoxIcon} />}
        description="Once you make gems as favorites they will be displayed here"
      />
    );
  }

  if (gems.length === 0 && !getGemsLoading && (search || selectedFilterOptions.length > 0)) {
    return (
      <CommonEmptyDataV1
        icon={<Box component="img" src={EmptyBoxIcon} />}
        title="No Gems found"
        // eslint-disable-next-line max-len
        description="  Sorry, we couldn't find any gems matching your search criteria. Please try adjusting your filters or searching with different keywords."
      />
    );
  }
  if (viewStatus === ViewType.GRID) {
    return (
      <GemGridView
        isLoading={getGemsLoading}
        enablePagePerTile
        gems={getFormattedGemTileList(gems)}
        expandedColumnValues={tileColumnValues}
        expandedColumns={tileColumns}
        enablePagination
        handleChangePage={handleChangePage}
        handleChangeTilesPerPage={handleChangeRowsPerPage}
        selectedPage={page}
        tilesPerList={[12, 18, 24]}
        selectedTilesPerPage={itemsPerPage}
        totalData={totalGemsCount}
        handleClickFullDetailButton={handleFullDetail}
        handleFavourite={toggleFavourite}
        handleTogglePublic={togglePublic}
        handleEditGem={handleEditGem}
      />
    );
  }
  if (viewStatus === ViewType.LIST) {
    return (
      <CustomTable
        columns={columns}
        enablePagePerRows
        enablePagination
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        isLoading={getGemsLoading}
        handleSortByChange={setSortBy}
        handleSortOrderChange={setSortOrder}
        rows={getFormattedGemList(gems)}
        rowsPerList={[10, 15, 20]}
        selectedPage={page}
        selectedRowsPerPage={itemsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        totalDataRows={totalGemsCount}
        tableType="Gems"
      />
    );
  }
}
